import type { Node } from '@milkdown/kit/prose/model';
import type { EditorView } from '@milkdown/kit/prose/view';

import type { MediaUploadHandler } from '../types';
import {
  dispatchAddUploadPlaceholder,
  dispatchRemoveUploadPlaceholder,
  findUploadPlaceholderWidgetRange,
  getPendingReplaceRangeForId,
  withRemoveUploadPlaceholderById,
  type UploadPlaceholderKind,
} from './uploadPlaceholderDecoration';
import { revokeUploadPlaceholderBlob, updateUploadProgressDom } from './uploadProgressDom';

/** Blob URL fallback when the app does not provide a real uploader. */
export const defaultMediaUpload: MediaUploadHandler = (file, onProgress) => {
  onProgress?.(1);

  return Promise.resolve(URL.createObjectURL(file));
};

/**
 * One end-to-end media upload in the editor: rich placeholder, optional progress, replace doc, cleanup.
 * Used by paste/drop (uploadPlugin) and video slash (slash-items).
 */
export interface RunEditorMediaUploadParams {
  view: EditorView;
  file: File;
  kind: UploadPlaceholderKind;
  /** Where the placeholder widget is anchored */
  widgetPos: number;
  /** If set, document range replaced after upload (e.g. current block for video slash) */
  replaceRange?: { from: number; to: number };
  upload: MediaUploadHandler;
  abortSignal?: AbortSignal;
  /** Build the final schema node; return null/undefined to remove placeholder only */
  createNode: (src: string, file: File) => Node | null | undefined;
  logLabel?: string;
  afterSuccess?: () => void;
}

export function runEditorMediaUpload(p: RunEditorMediaUploadParams): void {
  const id = crypto.randomUUID();

  dispatchAddUploadPlaceholder(p.view, {
    id,
    pos: p.widgetPos,
    kind: p.kind,
    file: p.file,
    ...(p.replaceRange && {
      replaceFrom: p.replaceRange.from,
      replaceTo: p.replaceRange.to,
    }),
  });

  const onProgress = (fraction: number) => updateUploadProgressDom(id, fraction);

  const teardownPlaceholder = () => {
    revokeUploadPlaceholderBlob(id);
    if (!p.view.isDestroyed) {
      dispatchRemoveUploadPlaceholder(p.view, id);
    }
  };

  const label = p.logLabel ?? '[mediaUpload]';

  void (async () => {
    try {
      const src = await p.upload(p.file, onProgress);

      if (p.abortSignal?.aborted || p.view.isDestroyed) {
        teardownPlaceholder();

        return;
      }

      const node = p.createNode(src, p.file);
      if (!node) {
        teardownPlaceholder();

        return;
      }

      const state = p.view.state;
      const pendingReplace = getPendingReplaceRangeForId(state, id);
      let tr = state.tr;

      if (pendingReplace) {
        tr = tr.replaceWith(pendingReplace.from, pendingReplace.to, node);
      } else {
        const widgetRange = findUploadPlaceholderWidgetRange(state, id);
        if (!widgetRange) {
          teardownPlaceholder();

          return;
        }
        tr = tr.replaceWith(widgetRange[0], widgetRange[1], node);
      }

      p.view.dispatch(withRemoveUploadPlaceholderById(tr, id).scrollIntoView());
      revokeUploadPlaceholderBlob(id);
      p.afterSuccess?.();
    } catch (e) {
      teardownPlaceholder();
      console.error(`${label} Upload failed:`, e);
    }
  })();
}
